import { useCallback, useEffect, useState } from "react";
import { usePrefersReducedMotion } from "../../platform/motion/usePrefersReducedMotion";
import { reactions } from "../avatar/reaction-bus";
import { useNarrowScreen } from "../../platform/ui/useNarrowScreen";
import { TYPING, startOf, type HeroMessage } from "./hero-messages";

export type Typing = { typed: number; complete: boolean };

/**
 * One message types itself out once the page is ready, after a pause that keeps it behind
 * the one before it. On a wide screen the messages follow one another a beat apart and all
 * stay up; on a phone each waits for the one before to be read, and replaces it beside her.
 * Nothing waits to be scrolled to: the home page is one window. With reduced motion the
 * whole line is simply there.
 *
 * The element is tracked with a callback ref rather than a ref object, so the observer is
 * attached when the element actually arrives and nothing is read during render.
 */
export function useTypedMessage(message: HeroMessage, order: number, ready: boolean) {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [seen, setSeen] = useState(false);
  const [state, setState] = useState<Typing>({ typed: 0, complete: false });
  const reducedMotion = usePrefersReducedMotion();
  const oneAtATime = useNarrowScreen();

  useEffect(() => {
    if (!element || !ready || seen) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setSeen(true);
      },
      { threshold: 0.6 },
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [element, ready, seen]);

  useEffect(() => {
    if (!seen) return;
    const timers: number[] = [];
    if (reducedMotion) {
      timers.push(
        window.setTimeout(() => {
          setState({ typed: message.text.length, complete: true });
        }, 0),
      );
    } else {
      const start = startOf(order, oneAtATime);
      timers.push(
        window.setTimeout(() => {
          if (message.reaction) {
            reactions.send(
              "gesture" in message.reaction
                ? { kind: "gesture", name: message.reaction.gesture }
                : { kind: "hold", name: message.reaction.hold },
            );
          }
        }, start),
      );
      if (message.reaction && "hold" in message.reaction) {
        timers.push(
          window.setTimeout(
            () => {
              reactions.send({ kind: "release" });
            },
            start + message.text.length * TYPING.perCharacter + TYPING.afterTyping,
          ),
        );
      }
      for (let character = 1; character <= message.text.length; character += 1) {
        timers.push(
          window.setTimeout(
            () => {
              setState({ typed: character, complete: character === message.text.length });
            },
            start + character * TYPING.perCharacter,
          ),
        );
      }
    }
    return () => {
      for (const timer of timers) window.clearTimeout(timer);
    };
  }, [seen, reducedMotion, oneAtATime, message, order]);

  const anchor = useCallback((node: HTMLElement | null) => {
    setElement(node);
  }, []);

  return { anchor, ...state };
}
